package ai.jessie.web.projects

import ai.jessie.shared.projects.PositionResponse
import ai.jessie.shared.projects.ProjectResponse
import ai.jessie.web.theme.ButtonSize
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.MemberActionsStyle
import ai.jessie.web.theme.PositionCardStyle
import ai.jessie.web.theme.PositionKindStyle
import ai.jessie.web.theme.PositionListStyle
import ai.jessie.web.theme.PositionNameStyle
import ai.jessie.web.theme.PositionStatsStyle
import ai.jessie.web.theme.StatBarStyle
import ai.jessie.web.theme.StatValueStyle
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLElement

// Step 12 and A1 (`docs/design/screens/05-Projekt.png`): the stat bar above the positions. Only
// what the server returned is shown. Activity and position operations need [actions] (detail page);
// after a save on the list page the stat bar also names and describes the project. On the
// detail page the description is the page subtitle.
@Composable
internal fun projectRecord(
    current: ProjectResponse,
    state: ProjectUiState,
    actions: ProjectActions?,
) {
    Div(attrs = CardStyle.toAttrs()) {
        TagElement<HTMLElement>("dl", StatBarStyle.toAttrs()) {
            if (actions == null) {
                stat("Name") { Text(current.name) }
                current.description?.let { stat("Beschreibung") { Text(it) } }
            }
            stat("Kunde") { Text(current.customerName ?: "Kein Kunde") }
            stat("Status") {
                activeBadge(current.active)
                billableBadge(current.billable)
            }
            stat("Positionen") {
                Span(attrs = StatValueStyle.toAttrs()) { Text(positionCount(current)) }
            }
            if (actions != null) projectActivity(state, actions)
        }
    }
    if (state.activityUnavailable && actions != null)
        projectStatus(
            "Aktivität und Statistik stehen in Jessie zurzeit nicht zur Verfügung. Das ist " +
                "keine Aussage über erfasste Arbeit in diesem Projekt."
        )
    H2 { Text("Positionen") }
    if (current.positions.isEmpty()) P(attrs = HintStyle.toAttrs()) { Text("Keine Positionen.") }
    else
        Ul(attrs = PositionListStyle.toAttrs()) {
            current.positions.forEach { positionCard(it, state, actions) }
        }
}

// A2, BR-056: figures only from the server; an unavailable integration is never shown as zero.
@Composable
private fun projectActivity(state: ProjectUiState, actions: ProjectActions) {
    val activity = state.activity
    if (activity == null)
        stat("Aktivität und Statistik") {
            themedButton(
                "Aktivität und Statistik anzeigen",
                actions.activity,
                size = ButtonSize.SMALL,
                disabled = state.busy,
            )
        }
    else {
        stat("Arbeitseinträge") {
            Span(attrs = StatValueStyle.toAttrs()) { Text(activity.workEntries.toString()) }
        }
        stat("Erfasste Dauer") {
            Span(attrs = StatValueStyle.toAttrs()) { Text(durationLabel(activity.durationSeconds)) }
        }
    }
}

// `components.md`, PositionCard: kind and billing, name, then the stored price and VAT. The
// operations keep their full labels; hours, offers and favourites have no contract.
@Composable
private fun positionCard(
    position: PositionResponse,
    state: ProjectUiState,
    actions: ProjectActions?,
) {
    val details = position.details
    Li(attrs = PositionCardStyle.toAttrs()) {
        Div {
            Div(attrs = PositionKindStyle.toAttrs()) {
                Span { Text(positionTypes[details.type] ?: "–") }
                billableBadge(details.billable)
                if (!details.active) activeBadge(false)
            }
            P(attrs = PositionNameStyle.toAttrs()) { Text(details.name) }
        }
        TagElement<HTMLElement>("dl", PositionStatsStyle.toAttrs()) {
            TagElement<HTMLElement>("dt", null) { Text(priceLabel(details.type)) }
            TagElement<HTMLElement>("dd", null) { Text(details.unitPrice ?: "–") }
            TagElement<HTMLElement>("dt", null) { Text("MWST") }
            TagElement<HTMLElement>("dd", null) {
                Text(position.vatName?.let { vatLabel(it, position.vatRate) } ?: "–")
            }
        }
        val id = details.id
        if (actions != null && id != null)
            Div(attrs = MemberActionsStyle.toAttrs()) {
                if (details.active)
                    themedButton(
                        "Position «${details.name}» deaktivieren",
                        { actions.deactivate(id) },
                        size = ButtonSize.SMALL,
                        disabled = state.busy,
                        elementId = deactivateId(id),
                    )
                themedButton(
                    "Position «${details.name}» entfernen",
                    { actions.remove(id) },
                    size = ButtonSize.SMALL,
                    disabled = state.busy,
                )
            }
    }
}

@Composable
private fun stat(term: String, value: @Composable () -> Unit) {
    Div {
        TagElement<HTMLElement>("dt", null) { Text(term) }
        TagElement<HTMLElement>("dd", null) { value() }
    }
}

private fun positionCount(current: ProjectResponse) = current.positions.size.toString()
